import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Container, Section, Eyebrow, Lede } from '../components/ui/Section';
import { useSiteContent } from '../hooks/useSiteContent';

const PageHeader = styled.section`
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.sectionPadding.md} 0 ${props => props.theme.sectionPadding.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const PageTitle = styled(motion.h1)`
  max-width: 14ch;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const PageSubtitle = styled(motion(Lede))`
  margin: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[10]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing[16]};
  }

  h3 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    margin-bottom: ${props => props.theme.spacing[5]};
    max-width: 14ch;
  }

  p {
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    max-width: 48ch;
  }
`;

/** Contact rows read as a list: hairline separated, label above value. */
const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing[4]};
  padding: ${props => props.theme.spacing[5]} 0;
  border-top: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  svg {
    color: ${props => props.theme.colors.primary[500]};
    flex-shrink: 0;
    margin-top: 2px;
    width: 18px;
    height: 18px;
  }

  div {
    h4 {
      font-family: ${props => props.theme.typography.fontFamily.body};
      font-size: ${props => props.theme.typography.fontSize.xs};
      font-weight: ${props => props.theme.typography.fontWeight.medium};
      letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
      text-transform: uppercase;
      color: ${props => props.theme.colors.textMuted};
      margin-bottom: ${props => props.theme.spacing[2]};
    }

    span {
      font-size: ${props => props.theme.typography.fontSize.lg};
      color: ${props => props.theme.colors.ink};
    }
  }
`;

const Contact: React.FC = () => {
  const { siteContent, isLoading } = useSiteContent();

  return (
    <div>
      <PageHeader>
        <Container>
          <Eyebrow>Contact</Eyebrow>
          <PageTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact Us
          </PageTitle>
          <PageSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Get in touch with us to learn more about our work or to get involved in making a difference.
          </PageSubtitle>
        </Container>
      </PageHeader>

      <Section $size="lg">
        <Container>
          <ContactGrid>
            <div>
              <h3>Get in touch</h3>
              <p>
                We'd love to hear from you. Whether you're interested in volunteering, partnering with us, or simply want to learn more about our work, don't hesitate to reach out.
              </p>
            </div>

            <div>
              <ContactItem>
                <Mail size={18} />
                <div>
                  <h4>Email</h4>
                  <span>{isLoading ? 'info@kuddy.org' : siteContent?.contactInfo.email}</span>
                </div>
              </ContactItem>

              <ContactItem>
                <Phone size={18} />
                <div>
                  <h4>Phone</h4>
                  <span>{isLoading ? '+1 (548) 390-4250' : siteContent?.contactInfo.phone}</span>
                </div>
              </ContactItem>

              <ContactItem>
                <MapPin size={18} />
                <div>
                  <h4>Address</h4>
                  <span>{isLoading ? 'Brampton, Ontario, Canada' : siteContent?.contactInfo.address}</span>
                </div>
              </ContactItem>
            </div>
          </ContactGrid>
        </Container>
      </Section>
    </div>
  );
};

export default Contact;